import { faqs, howItWorks } from "./helpers";
import { routes } from "@/global/routes";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import BackgroundImage from "public/images/timberrattlers/TimberRattlers-Background-Mar-24.webp";
import BannerImage from "public/images/timberrattlers/timberrattlersbark_banner_aug_24.png";
import LogoImage from "public/images/timberrattlers/TimberRattlers-Logo-Mar-24.png";
import { TeamConfig } from "../types";
import { vendorUUIDs } from "../../constants";
import StaticSeatMapImage from "public/images/timberrattlers/tim_rats_static_seat_map.png";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";

const config: TeamConfig = {
  teamSchema: "timberrattlers_flex",
  browserTitle: "Wisconsin Timber Rattlers",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Wisconsin Timber Rattlers",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 7,
        updatedAccountGroup: 0,
        discount: 0,
        customProgressBarMessage:
          "Congrats! You've unlocked your Timber Rattlers 7 game Flex Plan. Add 3 more games for 20% off your tickets or checkout now.",
      },
      {
        id: 2,
        minSelectedAmount: 8,
        updatedAccountGroup: 0,
        discount: 0,
        hideOfferFromModal: true,
        customProgressBarMessage: "Add 2 more games for 20% off your tickets.",
      },
      {
        id: 3,
        minSelectedAmount: 9,
        updatedAccountGroup: 0,
        discount: 0,
        hideOfferFromModal: true,
        customProgressBarMessage: "Add 1 more game for 20% off your tickets.",
      },
      {
        id: 4,
        minSelectedAmount: 10,
        updatedAccountGroup: 1,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You've unlocked 20% off your tickets. Add more games or checkout now.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    staticSeatMapImage: StaticSeatMapImage.src,
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 7,
    maxGameSelectionCount: 32,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: false,
    isPerksEnabled: true,

    defaultSeatCount: 2,

    hideViewTicketsInThankYouPage: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://www.milb.com/wisconsin",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  useFirstAndLastNames: false,

  // images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage.src + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
      overlayLogoWidth: 150,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: true,
      hideSectionFilter: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    viewTicketsUrl: "https://bit.ly/3WBUzcJ",
  },

  colors: {
    primary: "#ae1b25",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarText:
        "Add 7 games to complete your Flex Plan! For max savings add 10 games for 20% off your tickets.",
      progressBarAditionalText:
        " to continue. Add 10 games for 20% off your tickets!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Tickets will be delivered to your Timber Rattlers team account in January 2025.",

    giftCard: {
      title: "Give The Gift Of Broadway",
      createGiftCard: {
        description:
          "Share the magic of Broadway with a friend or loved one. Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift card is not refundable.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore account above.",
        },
        logout: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore.",
        },
        success: {
          description:
            "has been added to your Hippodrome Broadway Series in Baltimore Account Manager account!",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
